import config from '../../config';
import { apiFetch, authenticatedIdentity } from './apiFetch';
import { recoverSession } from './webSession';
import axios from 'axios';
import { authHeader } from '../_helpers';
import { machineSettingsService, machineSettingsRequestCache } from './machineSettings.service';
import { homeService } from './home.service';

const cache = machineSettingsRequestCache;
let currentIdentity;

function clear() {
    cache.clear();
}

function session() {
    const identity = authenticatedIdentity();

    if (identity !== currentIdentity) {
        clear();
        currentIdentity = identity;
    }

    return identity;
}

function path(kind, suffix = '') {
    if (!['machines', 'testers', 'jig-fixtures'].includes(kind))
        throw new Error('Unsupported asset kind.');

    return `/machining/master-data/${kind}${suffix}`;
}

function queryString(query) {
    const values = { ...query, page: query.page || 1, limit: query.limit || 25 };

    return new URLSearchParams(
        Object.keys(values)
            .sort()
            .filter(
                (key) =>
                    [
                        'page', 
                        'limit', 
                        'search', 
                        'category', 
                        'assetCondition', 
                        'active'
                    ].includes(key) &&
                    values[key] !== '' &&
                    values[key] !== undefined &&
                    values[key] !== null
            )
            .map((key) => [key, String(values[key])])
    ).toString();
}

async function request(url, identity, method = 'GET', values) {
    if (identity !== authenticatedIdentity()) throw new Error('Account changed.');

    const response = await apiFetch(`${config.apiUrl}${url}`, {
        expectedIdentity: identity,
        method,
        headers: { 'Content-Type': 'application/json' },
        ...(values !== undefined ? { body: JSON.stringify(values) } : {}),
    });

    let data;

    try {
        data = await response.json();
    } catch {
        data = {};
    }

    if (!response.ok) {
        if (response.status === 401 || response.status === 403) clear();

        throw Object.assign(new Error(data.error || data.message || 'Request failed.'), {
            status: response.status,
            fields: data.fields || {},
        });
    }

    return data;
}

function read(key, loader, force) {
    return force
        ? { data: cache.peek(key), revalidate: cache.get(key, loader, true) }
        : cache.getStaleWhileRevalidate(key, loader);
}

async function mutate(send) {
    const identity = session();

    clear();
    homeService.clearFloorDeckCache();

    try {
        return await send(identity);
    } finally {
        clear();
        homeService.clearFloorDeckCache();
    }
}

function attachmentPath(kind, id, attachmentId, content = false) {
    return path(
        kind,
        '/' +
            encodeURIComponent(id) +
            '/attachments' +
            (attachmentId === undefined ? '' : '/' + encodeURIComponent(attachmentId)) +
            (content ? '/content' : '')
    );
}

async function uploadAttachments(kind, id, files, onProgress) {
    return mutate(async (identity) => {
        const body = new FormData();

        files.forEach((file) => body.append('files', file));

        try {
            const response = await axios.post(config.apiUrl + attachmentPath(kind, id), body, {
                headers: authHeader(),
                onUploadProgress: (event) => {
                    if (identity === authenticatedIdentity() && event.total && onProgress)
                        onProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
                },
            });

            if (identity !== authenticatedIdentity()) throw new Error('Account changed.');

            return response.data;
        } catch (error) {
            if (identity !== authenticatedIdentity()) throw new Error('Account changed.');

            const status = error.response?.status;

            if (status === 401) {
                recoverSession(identity, 'Your session expired. Please sign in again.');
            }

            throw Object.assign(
                new Error(
                    error.response?.data?.error || error.message || 'Document upload failed.'
                ),
                { status }
            );
        }
    });
}

export const machiningMasterDataService = {
    list(kind, query = {}, force = false) {
        const identity = session();
        const url = `${path(kind)}?${queryString(query)}`;

        return read(JSON.stringify([identity, url]), () => request(url, identity), force);
    },
    detail(kind, id) {
        return request(path(kind, '/' + encodeURIComponent(id)), session());
    },
    categories(kind, force = false) {
        path(kind);
        const identity = session();

        return read(
            JSON.stringify([identity, kind, 'categories']),
            async () => {
                if (identity !== authenticatedIdentity()) throw new Error('Account changed.');

                try {
                    return kind === 'machines'
                        ? await machineSettingsService.categories(force).revalidate
                        : await request(path(kind, '/categories'), identity);
                } catch (error) {
                    if (error.status === 401 || error.status === 403) clear();
                    throw error;
                }
            },
            force
        );
    },
    create: (kind, values) => mutate((identity) => request(path(kind), identity, 'POST', values)),
    update: (kind, id, values) =>
        mutate((identity) =>
            request(path(kind, '/' + encodeURIComponent(id)), identity, 'PUT', values)
        ),
    setActive: (kind, id, active) =>
        mutate((identity) =>
            request(path(kind, '/' + encodeURIComponent(id) + '/active'), identity, 'PATCH', {
                active,
            })
        ),
    // The delegated service already invalidates the shared cache around writes.
    createCategory: (kind, values) => {
        path(kind);
        session();
        return kind === 'machines'
            ? machineSettingsService.createCategory(values)
            : mutate((identity) => request(path(kind, '/categories'), identity, 'POST', values));
    },
    updateCategory: (kind, id, values) => {
        path(kind);
        session();
        return kind === 'machines'
            ? machineSettingsService.updateCategory(id, values)
            : mutate((identity) =>
                  request(
                      path(kind, '/categories/' + encodeURIComponent(id)),
                      identity,
                      'PUT',
                      values
                  )
              );
    },
    deleteCategory: (kind, id) => {
        path(kind);
        session();
        return kind === 'machines'
            ? machineSettingsService.deleteCategory(id)
            : mutate((identity) =>
                  request(path(kind, '/categories/' + encodeURIComponent(id)), identity, 'DELETE')
              );
    },
    listAttachments: (kind, id) => request(attachmentPath(kind, id), session()),
    uploadAttachments,
    deleteAttachment: (kind, id, attachmentId) =>
        mutate((identity) => request(attachmentPath(kind, id, attachmentId), identity, 'DELETE')),
    async downloadAttachment(kind, id, attachmentId) {
        const identity = session();
        const response = await apiFetch(
            config.apiUrl + attachmentPath(kind, id, attachmentId, true),
            { expectedIdentity: identity }
        );

        if (!response.ok) {
            let data;

            try {
                data = await response.json();
            } catch {
                data = {};
            }

            throw Object.assign(
                new Error(data.error || data.message || 'Document download failed.'),
                { status: response.status }
            );
        }

        const blob = await response.blob();

        if (identity !== authenticatedIdentity()) throw new Error('Account changed.');
        
        return blob;
    },
    clear,
};
